import { Injectable } from "@angular/core";
import { DateTime, Settings } from "luxon";
import { UtilsService } from "src/app/shared/services/utils.service";
import { Adapter } from "../adapter";
import {
  LEAK_RULE_ID,
  PERIODICITY_RULE_ID,
  SMART_RULE_ID,
} from "./alert-definition.model";
import { Model } from "./model";
import { User } from "./user.model";

export enum AlertType {
  daily_profile = "daily_profile",
  duration = "duration",
  flow_profile = "flow_profile",
  // hourly_profile = "hourly_profile",
  volume = "volume",
  daily_volume = "daily_volume",
  monthly_volume = "monthly_volume",
  low_capacitor = "low_capacitor",
  meter_offline = "meter_offline",
  gateway_offline = "gateway_offline",
  leak_3p = "leak_3p",
  burst_3p = "burst_3p",
  t_from_pause_30_min = "t_from_pause_30_min",
  t_from_pause_60_min = "t_from_pause_60_min",
  periodicity = "periodicity",
}

export const SMART_ALERT_TYPES = [
  AlertType.daily_profile,
  AlertType.flow_profile,
];

export enum DeviceType {
  meter = "meter",
  gateway = "gateway",
}

export const maxDistance = {
  [AlertType.volume]: 999,
  [AlertType.daily_volume]: 99999,
  [AlertType.monthly_volume]: 500000,
  [AlertType.duration]: 36000,
  [AlertType.flow_profile]: 100,
  [AlertType.daily_profile]: 100,
  [AlertType.burst_3p]: null,
  [AlertType.leak_3p]: null,
  [AlertType.low_capacitor]: null,
  [AlertType.gateway_offline]: null,
  [AlertType.meter_offline]: null,
  [AlertType.periodicity]: 1440,
  [AlertType.t_from_pause_30_min]: 72,
  [AlertType.t_from_pause_60_min]: 72,
};

export interface AlertData {
  deviceLabel: string;
  devicePlaceLabel: string;
  detailsLabel: string;
  label: string;
}

export class Alert implements Model {
  public deviceLabel: string;
  public devicePlaceLabel: string;
  public detailsLabel: string;
  public label: string;

  public get ruleId(): number {
    let ruleId = null;

    switch (this.type) {
      case AlertType.burst_3p:
      case AlertType.leak_3p:
      case AlertType.t_from_pause_30_min:
      case AlertType.t_from_pause_60_min:
        ruleId = LEAK_RULE_ID;
        break;
      case AlertType.daily_profile:
      case AlertType.flow_profile:
        ruleId = SMART_RULE_ID;
        break;
      case AlertType.periodicity:
        ruleId = PERIODICITY_RULE_ID;
        break;
      default:
        ruleId = this.alert_definition_id;
        break;
    }

    return ruleId;
  }

  public get deviceType(): DeviceType {
    if (this.type === AlertType.gateway_offline) {
      return DeviceType.gateway;
    }
    return DeviceType.meter;
  }

  public get beginDateLabel() {
    let result = "-";
    if (this.begin_date) {
      result = new Intl.DateTimeFormat($localize.locale || "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // Pour avoir un format 24h
        timeZone: Settings.defaultZone.name,
      }).format(this.begin_date.toJSDate());
    }
    return result;
  }
  public get endDateLabel() {
    let result = $localize`never`;
    if (this.end_date) {
      result = new Intl.DateTimeFormat($localize.locale || "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // Pour avoir un format 24h
        timeZone: Settings.defaultZone.name,
      }).format(this.end_date.toJSDate());
    }
    return result;
  }
  public get notifiedOnLabel() {
    let result = $localize`never`;
    if (this.notified_on) {
      result = new Intl.DateTimeFormat($localize.locale || "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // Pour avoir un format 24h
        timeZone: Settings.defaultZone.name,
      }).format(this.notified_on.toJSDate());
    }
    return result;
  }
  public get endNotifiedOnLabel() {
    let result = $localize`never`;
    if (this.end_notified_on) {
      result = new Intl.DateTimeFormat($localize.locale || "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false, // Pour avoir un format 24h
        timeZone: Settings.defaultZone.name,
      }).format(this.end_notified_on.toJSDate());
    }
    return result;
  }

  constructor(
    public id: number,
    public type: AlertType,
    public is_active: boolean,
    public begin_date: DateTime,
    public end_date: DateTime,
    public notified_on: DateTime,
    public end_notified_on: DateTime,
    public distance: number,
    public flow: number,
    public device_uuid: string,
    public alert_definition_id: number
  ) {}

  public clone(): Alert {
    const clone = new Alert(
      this.id,
      this.type,
      this.is_active,
      this.begin_date,
      this.end_date,
      this.notified_on,
      this.end_notified_on,
      this.distance,
      this.flow,
      this.device_uuid,
      this.alert_definition_id
    );

    clone.label = this.label;
    clone.deviceLabel = this.deviceLabel;
    clone.devicePlaceLabel = this.devicePlaceLabel;
    clone.detailsLabel = this.detailsLabel;

    return clone;
  }

  public setData(data: AlertData) {
    if (!data) return;

    this.label = data.label;
    this.deviceLabel = data.deviceLabel;
    this.devicePlaceLabel = data.devicePlaceLabel;
    this.detailsLabel = data.detailsLabel;
  }

  public getLabel(user: User, utils: UtilsService): string {
    let label = null;

    let volume = null;
    let duration = null;
    switch (this.type) {
      case AlertType.meter_offline:
        label = $localize`:@@alert.model.meterOfflineLabel:Meter offline`;
        break;
      case AlertType.gateway_offline:
        label = $localize`:@@alert.model.gatewayOfflineLabel:Gateway offline`;
        break;
      case AlertType.low_capacitor:
        label = $localize`:@@alert.model.lowCapacitorLabel:Meter low battery`;
        break;
      case AlertType.volume:
        // distance = liters
        volume = utils.getVolumeFormattedWithUser(user, this.distance);
        label = $localize`:@@alert.model.volumeLabel:Continuous flow threshold exceeded by ${volume}:volume:`;
        break;
      case AlertType.duration:
        // distance = seconds
        duration = utils.getDurationFormatted(this.distance, 1, true);
        label = $localize`:@@alert.model.durationLabel:Continuous flow threshold exceeded ${duration}:duration:`;
        break;
      case AlertType.daily_volume:
        // distance = liters
        volume = utils.getVolumeFormattedWithUser(user, this.distance);
        label = $localize`:@@alert.model.dailyVolumeLabel:Daily volume exceeded by ${volume}:volume:`;
        break;
      case AlertType.monthly_volume:
        // distance = liters
        volume = utils.getVolumeFormattedWithUser(user, this.distance);
        label = $localize`:@@alert.model.monthlyVolumeLabel:Monthly volume exceeded by ${volume}:volume:`;
        break;
      case AlertType.burst_3p:
        label = $localize`:@@alert.model.burst3pLabel:Significant leak`;
        break;
      case AlertType.leak_3p:
      case AlertType.t_from_pause_30_min:
      case AlertType.t_from_pause_60_min:
        label = $localize`:alert.model.burst3pLabel:Leak`;
        break;
      case AlertType.periodicity:
        // flow = hourly flow
        let flow = utils.getHourlyFlowFormattedWithUser(user, this.flow);
        label = $localize`:@@alert.model.periodicityLabel:Periodic flow at ${flow}:flow:`;
        break;
      case AlertType.daily_profile:
      case AlertType.flow_profile:
        // distance = % of day time
        const durationInSeconds = (24 * 60 * 60 * this.distance) / 100;
        duration = utils.getDurationFormatted(durationInSeconds, 1, true);
        label = $localize`:@@alert.model.smartLabel:Unusual water usage lasting ${duration}:duration:`;
        break;
    }

    return label;
  }

  public getDetailsLabel(user: User, utils: UtilsService): string {
    let detailsLabel = null;

    let duration;
    switch (this.type) {
      case AlertType.periodicity:
        // distance = minutes
        duration = utils.getDurationFormatted(
          this.distance * 60,
          2,
          true,
          false
        );
        detailsLabel = $localize`:@@alert.model.periodicityDetails:Flow every ${duration}:duration:`;
        break;
      case AlertType.flow_profile:
        // flow = L / min
        let flow = utils.getFlowFormattedWithUser(user, this.flow);
        detailsLabel = $localize`:@@alert.model.flowProfileDetails:Concerned flow at ${flow}:flow:`;
      default:
        // no details
        break;
    }

    return detailsLabel;
  }

  public getSummary(user: User, utils: UtilsService): string {
    let summary = null;

    switch (this.type) {
      case AlertType.meter_offline:
        summary = $localize`:@@alert.model.meterOfflineSummary:Triggers if no data is received from the meter for over 2 days. This may indicate a connection issue or a non-functioning meter.`;
        break;
      case AlertType.gateway_offline:
        summary = $localize`:@@alert.model.gatewayOfflineSummary:Triggers if no communication from the gateway is detected. The issue may be with the gateway or the network. Try restarting your internet router or the gateway.`;
        break;
      case AlertType.low_capacitor:
        summary = $localize`:@@alert.model.lowCapacitorSummary:Triggers if the meter's energy level is too low. This may lead to interruptions in data transmission.`;
        break;
      case AlertType.volume:
        summary = $localize`:@@alert.model.volumeSummary:Triggers if water flows continuously and exceeds the volume limit you set. Useful to detect a forgotten tap or a leak.`;
        break;
      case AlertType.duration:
        summary = $localize`:@@alert.model.durationSummary:Triggers if water flows continuously and exceeds the duration limit you set. Useful to detect a forgotten tap or a leak.`;
        break;
      case AlertType.daily_volume:
        summary = $localize`:@@alert.model.dailyVolumeSummary:Triggers if your water use goes over the threshold you set during any continuous 24-hour period. Useful for keeping your water usage in check and spotting overages.`;
        break;
      case AlertType.monthly_volume:
        summary = $localize`:@@alert.model.monthlyVolumeSummary:Triggers if your water use goes over the threshold you set during any continuous 30-day period. Useful for keeping your water usage in check and spotting overages.`;
        break;
      case AlertType.burst_3p:
        summary = $localize`:@@alert.model.burst3pSummary:Generated and sent directly by the meter when it detects unusually high flow. May indicate a pipe burst or accidental overuse.`;
        break;
      case AlertType.leak_3p:
        summary = $localize`:@@alert.model.leak3pSummary:Alert generated and sent directly by the meter when it detects continuous water use without sufficient breaks. May indicate a leak, even a small one.`;
        break;
      case AlertType.t_from_pause_30_min:
      case AlertType.t_from_pause_60_min:
        summary = $localize`:@@alert.model.tFromPauseSummary:Automatically triggers when no period without water use has been detected for a long time. Useful to spot a leak or near-continuous consumption. Less reactive than other alerts and may take longer to activate.`;
        break;
      case AlertType.periodicity:
        summary = $localize`:@@alert.model.periodicitySummary:Automatically detects a series of water flows regularly spaced, often caused by a toilet flush that doesn’t shut off properly. An estimate of hourly water loss is provided if the alert is triggered.`;
        break;
      case AlertType.daily_profile:
        summary = $localize`:@@alert.model.dailyProfileSummary:Triggers if your total daily water use time is significantly higher than usual. It helps you spot unusual shifts in your daily routine. You can adjust the sensitivity to detect smaller or larger changes.`;
        break;
      case AlertType.flow_profile:
        summary = $localize`:@@alert.model.flowProfileSummary:Notifies you to unusual changes in flow rates, such as the appearance of a new flow or a sharp increase on a familiar one. Helps quickly detect unexpected usage. Sensitivity can be set to filter minor fluctuations or only the major ones.`;
        break;
    }

    return summary;
  }
}

@Injectable({
  providedIn: "root",
})
export class AlertAdapter implements Adapter<Alert> {
  adapt(item: any): Alert {
    return new Alert(
      item.id,
      item.type,
      item.is_active == 1 ? true : false,
      item.begin_date
        ? DateTime.fromISO(item.begin_date, { zone: Settings.defaultZone })
        : null,
      item.end_date
        ? DateTime.fromISO(item.end_date, { zone: Settings.defaultZone })
        : null,
      item.notified_on
        ? DateTime.fromISO(item.notified_on, { zone: Settings.defaultZone })
        : null,
      item.end_notified_on
        ? DateTime.fromISO(item.end_notified_on, { zone: Settings.defaultZone })
        : null,
      item.distance,
      item.flow,
      item.device_uuid,
      item.alert_definition_id
    );
  }
}
